package com.yendo.mx.ui.profile

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.ReceiptLong
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage
import com.yendo.mx.models.PedidoFirebase
import com.yendo.mx.ui.theme.RosaYendo
import com.yendo.mx.utils.formatPrecio
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth // 🚀 IMPORTANTE: Importamos Auth directo
import dev.gitlive.firebase.firestore.firestore
import dev.gitlive.firebase.firestore.Direction
import dev.gitlive.firebase.firestore.Source
import dev.gitlive.firebase.firestore.DocumentSnapshot
import kotlinx.coroutines.launch
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import com.yendo.mx.utils.BackHandler

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun HistorialPedidosScreen(onBack: () -> Unit) {
    BackHandler {
        onBack()
    }
    val isDark = isSystemInDarkTheme()
    val colorFondoGlobal = if (isDark) Color(0xFF121212) else Color(0xFFF8F8F8)
    val colorFondoTarjetas = if (isDark) Color(0xFF1E1E1E) else Color.White
    val colorTextoPrincipal = if (isDark) Color.White else Color.Black
    val colorTextoSecundario = if (isDark) Color(0xFFAAAAAA) else Color.Gray
    val colorDivisor = if (isDark) Color(0xFF2C2C2E) else Color(0xFFF0F0F0)

    var pedidoSeleccionado by remember { mutableStateOf<PedidoFirebase?>(null) }
    val historial = remember { mutableStateListOf<PedidoFirebase>() }
    var cargando by remember { mutableStateOf(true) }
    var cargandoMas by remember { mutableStateOf(false) }
    var haLlegadoAlFinal by remember { mutableStateOf(false) }
    var ultimoDocumentoCargado by remember { mutableStateOf<DocumentSnapshot?>(null) }
    var filtroSeleccionado by remember { mutableStateOf("TODOS") }
    var errorDebug by remember { mutableStateOf("") }

    val listState = rememberLazyListState()
    val currentUid = Firebase.auth.currentUser?.uid
    val scope = rememberCoroutineScope()

    // 🚀 FUNCIÓN MAESTRA DE CONSULTA PAGINADA Y FILTRADA
    fun cargarPedidos(esReinicio: Boolean) {
        if (currentUid == null || (cargandoMas && !esReinicio) || (haLlegadoAlFinal && !esReinicio)) return

        scope.launch {
            if (esReinicio) {
                cargando = true
                ultimoDocumentoCargado = null
                haLlegadoAlFinal = false
                historial.clear()
            } else {
                cargandoMas = true
            }

            try {
                val estadosAFiltrar = when(filtroSeleccionado) {
                    "ENTREGADOS" -> listOf("ENTREGADO", "CONCLUIDO", "COMPLETADO")
                    "CANCELADOS" -> listOf("CANCELADO", "RECHAZADO")
                    else -> listOf("ENTREGADO", "CONCLUIDO", "COMPLETADO", "CANCELADO", "RECHAZADO")
                }

                // 🚀 SINTAXIS EXPLÍCITA (Lambda única para evitar bugs de KMP)
                var query = Firebase.firestore.collection("Pedidos")
                    .where {
                        if (filtroSeleccionado == "MANDADITOS") {
                            all(
                                "idUsuario" equalTo currentUid,
                                "estadoActual" inArray estadosAFiltrar,
                                "tipoPedido" equalTo "MANDADITO"
                            )
                        } else {
                            all(
                                "idUsuario" equalTo currentUid,
                                "estadoActual" inArray estadosAFiltrar
                            )
                        }
                    }
                    .orderBy("tiempos.creado", Direction.DESCENDING)
                    .limit(15)

                if (ultimoDocumentoCargado != null && !esReinicio) {
                    query = query.startAfter(ultimoDocumentoCargado!!)
                }

                val snapshot = query.get(Source.DEFAULT)
                val nuevosPedidos = snapshot.documents.mapNotNull { doc ->
                    try { doc.data<PedidoFirebase>() } catch (e: Exception) { null }
                }

                if (snapshot.documents.size < 15) {
                    haLlegadoAlFinal = true
                }

                if (snapshot.documents.isNotEmpty()) {
                    ultimoDocumentoCargado = snapshot.documents.last()
                }

                historial.addAll(nuevosPedidos)

            } catch (e: Exception) {
                errorDebug = "Error: ${e.message}"
                println("❌ Error cargando historial: ${e.message}")
            } finally {
                cargando = false
                cargandoMas = false
            }
        }
    }

    // 🚀 TRIGGER 1: Cuando el usuario cambia de Chip o inicia sesión
    LaunchedEffect(currentUid, filtroSeleccionado) {
        cargarPedidos(esReinicio = true)
    }

    // 🚀 DETECCIÓN DE SCROLL INFINITO
    val debeCargarMas by remember {
        derivedStateOf {
            val lastVisibleItem = listState.layoutInfo.visibleItemsInfo.lastOrNull()
            lastVisibleItem != null && lastVisibleItem.index >= historial.size - 2
        }
    }

    // 🚀 TRIGGER 2: Cuando el usuario llega al final de la lista
    LaunchedEffect(debeCargarMas) {
        if (debeCargarMas && !cargando && !cargandoMas && !haLlegadoAlFinal) {
            cargarPedidos(esReinicio = false)
        }
    }

    val pSel = pedidoSeleccionado
    if (pSel != null) {
        DetallePedidoScreen(
            pedidoId = pSel.idPedido,
            estado = pSel.estadoActual,
            onBack = { pedidoSeleccionado = null }
        )
    } else {
        Scaffold(
            topBar = {
                TopAppBar(
                    title = { Text("Mis Pedidos", fontWeight = FontWeight.ExtraBold, color = colorTextoPrincipal) },
                    navigationIcon = {
                        IconButton(onClick = onBack) {
                            Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Atrás", tint = colorTextoPrincipal)
                        }
                    },
                    colors = TopAppBarDefaults.topAppBarColors(containerColor = colorFondoGlobal)
                )
            },
            containerColor = colorFondoGlobal
        ) { padding ->
            Column(modifier = Modifier.fillMaxSize().padding(padding)) {
                // 🚀 CHIPS DE FILTRADO (SCROLLABLE)
                LazyRow(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(vertical = 8.dp),
                    contentPadding = PaddingValues(horizontal = 16.dp),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    val categorias = listOf(
                        "TODOS" to "Todos",
                        "ENTREGADOS" to "Entregados",
                        "CANCELADOS" to "Cancelados",
                        "MANDADITOS" to "Envíos"
                    )
                    items(categorias) { (id, label) ->
                        FilterChip(
                            selected = filtroSeleccionado == id,
                            onClick = {
                                if (filtroSeleccionado != id) {
                                    filtroSeleccionado = id
                                }
                            },
                            label = { Text(label, fontSize = 12.sp, maxLines = 1) },
                            colors = FilterChipDefaults.filterChipColors(
                                selectedContainerColor = RosaYendo.copy(alpha = 0.1f),
                                selectedLabelColor = RosaYendo
                            )
                        )
                    }
                }

                if (cargando) {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator(color = RosaYendo)
                    }
                } else if (historial.isEmpty()) {
                    Column(
                        modifier = Modifier.fillMaxSize(),
                        verticalArrangement = Arrangement.Center,
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Icon(Icons.AutoMirrored.Filled.ReceiptLong, null, tint = colorTextoSecundario.copy(alpha = 0.5f), modifier = Modifier.size(80.dp))
                        Spacer(Modifier.height(16.dp))
                        Text("Aún no tienes historial", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = colorTextoSecundario)
                        Text("Tus pedidos terminados aparecerán aquí.", color = colorTextoSecundario, fontSize = 14.sp)

                        if (errorDebug.isNotEmpty()) {
                            Spacer(Modifier.height(24.dp))
                            Text("DEBUG ERROR:", color = Color.Red, fontWeight = FontWeight.Bold, fontSize = 12.sp)
                            Text(errorDebug, color = Color.Red, fontSize = 10.sp, modifier = Modifier.padding(horizontal = 32.dp))
                        }
                    }
                } else {
                    LazyColumn(
                        modifier = Modifier.fillMaxSize(),
                        state = listState,
                        contentPadding = PaddingValues(16.dp),
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        items(historial) { pedido ->
                            ItemPedidoCard(
                                pedido = pedido,
                                isDark = isDark,
                                colorFondoTarjetas = colorFondoTarjetas,
                                colorTextoPrincipal = colorTextoPrincipal,
                                colorDivisor = colorDivisor,
                                onSeleccionar = { pedidoSeleccionado = it }
                            )
                        }

                        if (cargandoMas) {
                            item {
                                Box(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(16.dp),
                                    contentAlignment = Alignment.Center
                                ) {
                                    CircularProgressIndicator(
                                        color = RosaYendo,
                                        modifier = Modifier.size(24.dp)
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun ItemPedidoCard(
    pedido: PedidoFirebase,
    isDark: Boolean,
    colorFondoTarjetas: Color,
    colorTextoPrincipal: Color,
    colorDivisor: Color,
    onSeleccionar: (PedidoFirebase) -> Unit
) {
    // 🚀 LÓGICA DE PRECIO FINAL (FALLBACK)
    val precioFinalReal = pedido.desgloseCliente.totalPagadoFinal 
        ?: pedido.desgloseCliente.totalPagadoFinalSnake 
        ?: pedido.totalPagadoFinal 
        ?: pedido.totalPagadoFinalSnake 
        ?: pedido.desgloseCliente.totalPagado

    val estadoUpper = pedido.estadoActual.trim().uppercase()
    val colorEstado = when (estadoUpper) {
        "ENTREGADO", "CONCLUIDO", "COMPLETADO" -> Color(0xFF00C853)
        "CANCELADO", "RECHAZADO" -> Color(0xFFD50000)
        else -> Color(0xFF9E9E9E)
    }

    val fechaFormateada = remember(pedido.tiempos.creado) {
        try {
            val instant = Instant.fromEpochMilliseconds(pedido.tiempos.creado)
            val dateTime = instant.toLocalDateTime(TimeZone.currentSystemDefault())
            val dia = dateTime.dayOfMonth.toString().padStart(2, '0')
            val mes = dateTime.month.name.take(3).lowercase().replaceFirstChar { it.uppercase() }
            val hora = dateTime.hour.toString().padStart(2, '0')
            val min = dateTime.minute.toString().padStart(2, '0')
            "$dia $mes • $hora:$min"
        } catch (e: Exception) {
            "Fecha desconocida"
        }
    }

    Card(
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onSeleccionar(pedido) },
        colors = CardDefaults.cardColors(containerColor = colorFondoTarjetas),
        elevation = CardDefaults.cardElevation(defaultElevation = if (isDark) 0.dp else 2.dp),
        shape = RoundedCornerShape(16.dp),
        border = if (isDark) BorderStroke(1.dp, colorDivisor) else null
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // 🚀 1. LOGOTIPO DEL NEGOCIO
            Box(
                modifier = Modifier
                    .size(46.dp)
                    .clip(CircleShape)
                    .background(Color.LightGray.copy(alpha = 0.2f)),
                contentAlignment = Alignment.Center
            ) {
                if (pedido.logoRestaurante.isNotBlank()) {
                    AsyncImage(
                        model = pedido.logoRestaurante,
                        contentDescription = "Logo",
                        contentScale = ContentScale.Crop,
                        modifier = Modifier.fillMaxSize()
                    )
                } else {
                    Icon(
                        imageVector = Icons.Default.Storefront,
                        contentDescription = null,
                        tint = Color.Gray,
                        modifier = Modifier.size(20.dp)
                    )
                }
            }

            Spacer(Modifier.width(12.dp))

            // 🚀 2. INFORMACIÓN CENTRAL (Nombre, Fecha • Estado • Delivery/Pickup con Vector Icons)
            Column(modifier = Modifier.weight(1f)) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(
                        text = pedido.nombreRestaurante, 
                        fontWeight = FontWeight.ExtraBold, 
                        fontSize = 15.sp, 
                        color = colorTextoPrincipal,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.weight(1f, fill = false)
                    )
                    Spacer(Modifier.width(6.dp))
                    
                    val isDelivery = pedido.tipoPedido.uppercase() == "DELIVERY"
                    val tagBg = if (isDelivery) Color(0xFF2196F3).copy(alpha = 0.15f) else Color(0xFF4CAF50).copy(alpha = 0.15f)
                    val tagColor = if (isDelivery) Color(0xFF2196F3) else Color(0xFF4CAF50)
                    
                    Surface(
                        color = tagBg,
                        shape = RoundedCornerShape(4.dp)
                    ) {
                        Row(
                            modifier = Modifier.padding(horizontal = 4.dp, vertical = 2.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(
                                imageVector = if (isDelivery) Icons.Default.Moped else Icons.Default.ShoppingBag,
                                contentDescription = null,
                                tint = tagColor,
                                modifier = Modifier.size(11.dp)
                            )
                            Spacer(Modifier.width(2.dp))
                            Text(
                                text = if (isDelivery) "Delivery" else "Pickup",
                                color = tagColor,
                                fontSize = 9.sp,
                                fontWeight = FontWeight.Black
                            )
                        }
                    }
                }
                Spacer(Modifier.height(2.dp))
                Text(
                    text = "$fechaFormateada • ${com.yendo.mx.utils.formatEstadoHuman(pedido.estadoActual)}", 
                    color = colorEstado, 
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Medium
                )
            }

            Spacer(Modifier.width(8.dp))

            // 🚀 3. PRECIO FINAL
            Text(
                text = "$${formatPrecio(precioFinalReal)}",
                fontWeight = FontWeight.Black,
                fontSize = 15.sp,
                color = colorTextoPrincipal
            )
        }
    }
}

